import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/PhpAuthContext';
import { useHospitalBranding } from '../../contexts/HospitalBrandingContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from '../../components/ui/use-toast';
import { Stethoscope, Phone, Lock, Eye, EyeOff } from 'lucide-react';

const PatientLoginPage = () => {
  const [formData, setFormData] = useState({ mobileNumber: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn, profile } = useAuth();
  const { hospitalSlug: urlSlug } = useParams();
  const { branding } = useHospitalBranding();
  const navigate = useNavigate();
  const loginSuccessRef = React.useRef(false);
  
  // Get hospital slug from URL params or branding context
  const hospitalSlug = urlSlug || branding?.slug;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.mobileNumber || !formData.password) {
      toast({
        title: 'Error',
        description: 'Please enter your mobile number and password',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      // Get hospital slug from URL params or branding context
      const slug = hospitalSlug || branding?.slug;
      
      // Use mobile number as username for patient login
      // Pass hospital_slug to validate patient belongs to this hospital
      const result = await signIn(formData.mobileNumber, formData.password, slug);
      if (!result.error) {
        loginSuccessRef.current = true;
        toast({
          title: 'Welcome back!',
          description: 'Successfully logged in.',
        });
        
        // Navigate to slug-based patient dashboard if slug is available, otherwise use legacy route
        const dashboardPath = slug ? `/${slug}/patient/dashboard` : '/patient/dashboard';
        console.log('PatientLoginPage: Login successful, navigating to:', dashboardPath);
        navigate(dashboardPath, { replace: true });
      } else {
        loginSuccessRef.current = false;
        toast({
          title: 'Login Failed',
          description: result.error?.message || 'Invalid mobile number or password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Login Failed',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Only redirect if we're on the patient login page and user successfully logs in
  // This is a fallback in case the immediate navigation in handleSubmit doesn't work
  React.useEffect(() => {
    // Only redirect if we're actually on the patient login page
    const currentPath = window.location.pathname;
    if (currentPath !== '/patient/login' && !currentPath.includes('/patient/login')) {
      return; // Don't redirect if we're not on the patient login page
    }
    
    // Only redirect if login was successful (to avoid redirecting on page load)
    if (profile && loginSuccessRef.current) {
      console.log('PatientLoginPage useEffect: Profile detected after login, redirecting based on role:', profile.role, 'Profile data:', profile);
      
      // Check role - handle both explicit role and inferred role
      const userRole = profile.role || (profile.patient_unique_id ? 'patient' : null) || (profile.table === 'patients' ? 'patient' : null);
      
      console.log('PatientLoginPage useEffect: Detected role:', userRole);
      
      if (userRole === 'patient') {
        console.log('PatientLoginPage useEffect: Redirecting to patient dashboard');
        // Get hospital slug from URL params or branding context
        const slug = hospitalSlug || branding?.slug;
        
        // Navigate to slug-based patient dashboard if slug is available, otherwise use legacy route
        const dashboardPath = slug ? `/${slug}/patient/dashboard` : '/patient/dashboard';
        console.log('PatientLoginPage useEffect: Redirecting to:', dashboardPath);
        
        // Use a small delay to ensure navigation happens after state update
        const redirectTimer = setTimeout(() => {
          navigate(dashboardPath, { replace: true });
          loginSuccessRef.current = false; // Reset flag after navigation
        }, 200);
        
        return () => clearTimeout(redirectTimer);
      } else {
        // If somehow a non-patient logs in through this page, redirect to their dashboard
        console.log('PatientLoginPage useEffect: Non-patient role detected:', userRole, '- redirecting to appropriate dashboard');
        const redirectTimer = setTimeout(() => {
          switch (userRole) {
            case 'doctor':
            case 'chief_doctor':
              navigate('/doctor/dashboard', { replace: true });
              break;
            case 'admin':
              navigate('/admin/dashboard', { replace: true });
              break;
            default:
              console.warn('PatientLoginPage useEffect: Unknown role, redirecting to home');
              navigate('/', { replace: true });
          }
          loginSuccessRef.current = false; // Reset flag after navigation
        }, 200);
        
        return () => clearTimeout(redirectTimer);
      }
    }
  }, [profile, navigate, hospitalSlug, branding]);

  // Debug logging
  React.useEffect(() => {
    console.log('PatientLoginPage: Branding state:', branding);
    console.log('PatientLoginPage: Hospital slug from params:', hospitalSlug);
  }, [branding, hospitalSlug]);

  return (
    <>
      <Helmet>
        <title>Patient Login - {branding?.name || 'Dental Hub'}</title>
        <meta name="description" content="Sign in to your patient portal to access your dental health records and manage your appointments." />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          <Card className="glass-effect border-gray-700 shadow-2xl">
            <CardHeader className="text-center p-4 sm:p-6">
              <div className="flex items-center justify-center space-x-2 mb-3 sm:mb-4">
                {branding?.logoUrl ? (
                  <img 
                    src={branding.logoUrl} 
                    alt={`${branding.name || 'Hospital'} Logo`}
                    className="h-8 w-8 sm:h-12 sm:w-12 object-contain"
                    onError={(e) => {
                      console.error('PatientLoginPage: Logo load error:', e);
                      e.target.style.display = 'none';
                      const fallback = e.target.nextElementSibling;
                      if (fallback) fallback.style.display = 'flex';
                    }}
                    onLoad={() => console.log('PatientLoginPage: Logo loaded successfully')}
                  />
                ) : null}
                <div className={`${branding?.logoUrl ? 'hidden' : 'flex'} items-center justify-center h-8 w-8 sm:h-12 sm:w-12 bg-blue-600 rounded-lg`}>
                  <Stethoscope className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <span className="text-xl sm:text-2xl font-bold gradient-text">
                  {branding?.name || 'Dental Hub'}
                </span>
              </div>
              <CardTitle className="text-xl sm:text-2xl text-white">Patient Portal</CardTitle>
              <CardDescription className="text-gray-300 text-sm sm:text-base">
                Sign in with your mobile number to access your dashboard
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="mobileNumber" className="text-white">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="mobileNumber"
                      type="tel"
                      placeholder="Enter your mobile number"
                      value={formData.mobileNumber}
                      onChange={(e) => {
                        // Allow only numbers, +, -, spaces, and parentheses
                        const value = e.target.value.replace(/[^0-9+\-() ]/g, '');
                        setFormData({ ...formData, mobileNumber: value });
                      }}
                      className="pl-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    Enter the mobile number registered with your account
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 z-10" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 bg-gray-800 border-gray-600 text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 h-4 w-4 text-gray-400 hover:text-gray-300 focus:outline-none"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full medical-gradient text-white hover:opacity-90"
                  disabled={loading}
                >
                  {loading ? 'Signing In...' : 'Login'}
                </Button>
              </form>

              <div className="mt-6 text-center space-y-2">
                <p className="text-gray-400 text-sm">
                  Forgot your password?{' '}
                  <span className="text-blue-400">Contact your clinic administrator</span>
                </p>
                <p className="text-gray-400 text-sm">
                  Are you a doctor or staff?{' '}
                  <Link 
                    to={hospitalSlug ? `/${hospitalSlug}/login` : '/login'} 
                    className="text-blue-400 hover:text-blue-300 underline"
                  >
                    Click here to login
                  </Link>
                </p>
                <p className="text-gray-400 text-sm mt-4">
                  <Link 
                    to={hospitalSlug ? `/${hospitalSlug}` : '/'} 
                    className="text-blue-400 hover:text-blue-300 underline transition-colors"
                  >
                    ← Back to Home
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </>
  );
};

export default PatientLoginPage;

